import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Select, MenuItem, FormControl, Box, Typography, ListItemText, ListItemIcon, Chip
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { fetchAccountsLookup, setSelectedAccount } from '../../store/cloudSlice';
import useRefreshTick from '../../hooks/useRefreshTick';

const PROVIDER_COLORS = { aws: '#ff9900', azure: '#0078d4', gcp: '#4285f4' };

export default function AccountSelector() {
  const refreshTick = useRefreshTick();
  const dispatch = useDispatch();
  const { accountsLookup, selectedProvider, selectedAccountId } = useSelector((s) => s.cloud);
  const { user } = useSelector((s) => s.auth);

  // Refresh lookup whenever the user logs in or the provider filter changes
  useEffect(() => {
    if (!user) return;
    const params = selectedProvider && selectedProvider !== 'all'
      ? { provider: selectedProvider }
      : {};
    dispatch(fetchAccountsLookup(params));
  }, [dispatch, user, selectedProvider, refreshTick]);

  // Filter lookup client-side as a defence-in-depth measure
  const visibleAccounts = useMemo(() => {
    if (!Array.isArray(accountsLookup)) return [];
    if (!selectedProvider || selectedProvider === 'all') return accountsLookup;
    return accountsLookup.filter(a => a.cloud_provider === selectedProvider);
  }, [accountsLookup, selectedProvider]);

  const value = selectedAccountId || 'all';

  const handleChange = (e) => {
    const v = e.target.value;
    dispatch(setSelectedAccount(v === 'all' ? null : v));
  };

  return (
    <FormControl size="small" sx={{ minWidth: { xs: 160, sm: 220 } }}>
      <Select
        value={value}
        onChange={handleChange}
        displayEmpty
        renderValue={(val) => {
          if (val === 'all') {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountTreeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  All Accounts
                </Typography>
                <Chip
                  label={visibleAccounts.length}
                  size="small"
                  sx={{ height: 18, fontSize: 11, ml: 0.5, backgroundColor: 'primary.light', color: 'primary.dark' }}
                />
              </Box>
            );
          }
          const acct = accountsLookup.find(a => a.id === val);
          if (!acct) return <Typography variant="body2">Select Account</Typography>;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: PROVIDER_COLORS[acct.cloud_provider] || '#94a3b8',
                  flexShrink: 0
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {acct.account_name || acct.account_id}
              </Typography>
            </Box>
          );
        }}
        inputProps={{ 'aria-label': 'Cloud account' }}
        sx={{ '& .MuiSelect-select': { py: 0.8 } }}
      >
        <MenuItem value="all">
          <ListItemIcon sx={{ minWidth: 28 }}>
            <AccountTreeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText
            primary="All Accounts"
            secondary={`${visibleAccounts.length} accounts`}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 11 }}
          />
        </MenuItem>
        {visibleAccounts.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              primary="No accounts available"
              primaryTypographyProps={{ fontSize: 13, fontStyle: 'italic' }}
            />
          </MenuItem>
        )}
        {visibleAccounts.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box
                sx={{
                  width: 10, height: 10, borderRadius: '50%',
                  backgroundColor: PROVIDER_COLORS[a.cloud_provider] || '#94a3b8'
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={a.account_name || a.account_id}
              secondary={`${(a.cloud_provider || '').toUpperCase()} · ${a.asset_count ?? 0} assets · ${a.finding_count ?? 0} findings`}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
